import React, { Fragment, useEffect, useState } from 'react';
import {
  ButtonWithAction,
  SidebarSingleContent,
  Box,
  Cluster,
  constants,
  Center,
  BoxWithShadow,
  SolidDivider,
  Spinner,
  TypeaheadInput,
  Text,
  Title,
} from '@thecb/components';
import { HashLink } from 'react-router-hash-link';
import AlertBar from '../../components/alert-bar';
import CartSidebar from '../../components/cart-sidebar';
import ProductsSection from './ProductsSection';
import { AgencyTileWrapper } from './Products.styled';
import { useSelector } from 'react-redux';
import SharedAlertBar from '../../components/alert-bar/SharedAlertBar';

const Header = ({ alertBar, alertBarActions }) => {
  const { alerts: sharedAlerts } = useSelector((state) => state.shared);
  return (
    <Box width="100%" padding="16px 0">
      <Center maxWidth="85.714rem">
        {alertBar.alerts.length > 0 && (
          <Box padding="16px 0">
            <AlertBar {...alertBar} {...alertBarActions} />
          </Box>
        )}
        {sharedAlerts.length > 0 && (
          <Box padding="16px 0">
            <SharedAlertBar />
          </Box>
        )}
        <Title
          color={constants.colors.CHARADE_GREY}
          weight={constants.fontWeights.FONT_WEIGHT_BOLD}
        >
          Search
        </Title>
      </Center>
    </Box>
  );
};

const AgencyTile = ({ text, id }) => {
  return (
    <AgencyTileWrapper rebrand>
      <HashLink smooth to={`#agency-${id}`}>
        {text}
      </HashLink>
    </AgencyTileWrapper>
  );
};

const Products = ({
  fetchProducts,
  fetchAgencies,
  clearCurrentProduct,
  currentProduct,
  products,
  agencies,
  currentAgencyInCart,
  alertBars,
  actions,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(products.data);
  const [filteredAgencies, setFilteredAgencies] = useState(agencies.data);
  const COLLAPSED_ALL = 'COLLAPSED_ALL';
  const EXPANDED_ALL = 'EXPANDED_ALL';
  const [expandCollapseState, setExpandCollapseState] = useState({
    lastEvent: EXPANDED_ALL,
  });
  useEffect(() => {
    if (!products.data.length) {
      fetchProducts();
      fetchAgencies();
    }
  }, []);
  useEffect(() => {
    if (Object.keys(currentProduct.form_data).length) {
      clearCurrentProduct();
    }
  }, []);

  useEffect(() => {
    const nextFilteredProducts = products.data.filter((product) =>
      product.name.match(new RegExp(searchValue, 'i')),
    );
    const nextFilteredAgencies = agencies.data.filter((agency) =>
      nextFilteredProducts.some((product) => product.agency_id === agency.id),
    );
    const cartFilteredAgencies = currentAgencyInCart
      ? nextFilteredAgencies.filter(
          (agency) => agency.id === currentAgencyInCart,
        )
      : nextFilteredAgencies;
    setFilteredAgencies(cartFilteredAgencies);
    setFilteredProducts(nextFilteredProducts);
  }, [searchValue, agencies, products, currentAgencyInCart]);

  const agenciesSection = (
    <Fragment>
      <Box padding="1rem 0" id="agency_links">
        <Title
          variant="h6"
          weight={constants.fontWeights.FONT_WEIGHT_BOLD}
          extraStyles={`padding-bottom: .5rem; font-size: 1rem;`}
          // color={themeValues.titleColor}
          as="h6"
        >
          Scroll to Agency
        </Title>
        {agencies.fetching ? (
          <Spinner size="100" />
        ) : (
          <Cluster
            justify="flex-start"
            align="stretch"
            childGap="7px"
            extraStyles={'padding: 1rem 0;'}
          >
            {filteredAgencies.map((agency) => (
              <AgencyTile
                key={agency.id}
                text={agency.description}
                id={agency.id}
              />
            ))}
          </Cluster>
        )}
      </Box>
      <SolidDivider />
    </Fragment>
  );

  const handleTypeaheadChange = (value) => setSearchValue(value);
  const searchInput = (
    <Box padding="0 0 1rem">
      <TypeaheadInput
        handleChange={handleTypeaheadChange}
        labelText="Products search"
        placeholder="Search..."
        id="products-search"
      />
    </Box>
  );

  const expandCollapseAllSection = (
    <Fragment>
      <Box padding="1rem 0" id="agency_links">
        <Cluster justify="space-between" align="center">
          <Title
            weight={constants.fontWeights.FONT_WEIGHT_BOLD}
            // color={themeValues.titleColor}
            as="h6"
            extraStyles={'font-size: 1.125rem'}
          >
            All Tiles
          </Title>
          <Cluster align="center">
            <ButtonWithAction
              variant="ghost"
              action={() => {
                setExpandCollapseState({ lastEvent: EXPANDED_ALL });
              }}
              text="Expand All"
              extraStyles={'min-width: 100px;'}
              textExtraStyles={'font-weight: 200; font-size: 1rem'}
            />
            |
            <ButtonWithAction
              variant="ghost"
              action={() => {
                setExpandCollapseState({ lastEvent: COLLAPSED_ALL });
              }}
              text="Collapse All"
              extraStyles={'min-width: 100px;'}
              textExtraStyles={'font-weight: 200; font-size: 1rem'}
            />
          </Cluster>
        </Cluster>
      </Box>
      <SolidDivider />
    </Fragment>
  );

  const isFetchingProductsOrAgencies = agencies.fetching || products.fetching;
  const content = (
    <Box padding=".5rem 0 1.5rem">
      <BoxWithShadow
        background={constants.colors.WHITE}
        variant="baseStandard"
        padding="1.5rem"
        borderRadius="0.25rem"
      >
        {searchInput}
        <SolidDivider />
        {agenciesSection}
        {expandCollapseAllSection}
        {isFetchingProductsOrAgencies ? (
          <Spinner size="100" />
        ) : (
          filteredAgencies.map((agency) => (
            <ProductsSection
              universalExpandCollapseState={expandCollapseState}
              key={agency.id}
              id={agency.id}
              title={agency.description}
              products={filteredProducts.filter(
                (product) => product.agency_id === agency.id,
              )}
            />
          ))
        )}
        {!isFetchingProductsOrAgencies && !filteredAgencies.length && (
          <Box padding="16px 0">
            <Text color={constants.colors.GREY_CHATEAU}>No Data</Text>
          </Box>
        )}
      </BoxWithShadow>
    </Box>
  );

  return (
    <SidebarSingleContent
      header={
        <Header
          key="ProductsHeader"
          alertBar={alertBars.productsAlertBar}
          alertBarActions={actions.alertBars.productsAlertBar}
        />
      }
      mainContent={content}
      sidebarContent={<CartSidebar />}
      sidebarTargetWidth="27.429rem"
      sidebarWrapperMaxWidth="85.714rem"
      sidebarOnRight={true}
      childGap="0.5rem"
    />
  );
};

export default Products;
